import React, {useMemo, useState} from 'react';
import {scalableOutputTypes} from '@/views/storyboard/utils/chartConstants';
import {getChartDataTotal} from '@/views/storyboard/utils/calculations/chartTotal';
import {getScaledChartData} from '@/views/storyboard/utils/calculations/chartScaling';
import {addLinearRegressionToData} from '@/views/storyboard/utils/calculations/chartLinearRegression';
import {ChartAggregationCalcType} from '@/views/storyboard/utils/types';
import {addMovingAverageToData} from '@/views/storyboard/utils/calculations/chartMovingAverage';
import {ChartComponentProps} from '@/views/storyboard/charts/ChartContainer';
import {
    MetricTimelineAggregatedChartData, MetricTimelineHistogramChartData,
    MultiTermsTimelineChartData,
    TermsTimelineChartData,
    TimelineByDateChartData
} from 'entities/chartData';
import {DataLabelPosition} from 'entities/chart';
import {notRenderedKeysArray} from '../utils/SortLegendKeys';
import AreaCharts from './AreaChart';
import ChartIndicators from '../utils/ChartIndicators';
import {debounce} from 'lodash';
import {getFooterAggregationType} from '@/views/storyboard/charts/utils/chartFooterData';

export type AreaChartData = (
    TimelineByDateChartData
    | TermsTimelineChartData
    | MultiTermsTimelineChartData
    | MetricTimelineAggregatedChartData
    | MetricTimelineHistogramChartData
    );

const AreaChartWrapper = React.memo<ChartComponentProps>((
    {
        dataLooselyTyped,
        height,
        width,
        title,
        linearRegression,
        comparablePeriod,
        movingAverageDays = null,
        movingAverageDaysCenter,
        scalingFactor,
        dataLabelPosition = DataLabelPosition.Off,
        chartQuery,
        queryDuration = '0',
        chartSettingsView,
        description = '',
        datePeriod = '',
        onPreviewFooterChange,
    },
) => {

    const data = dataLooselyTyped as AreaChartData[];
    const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
    const debouncedSetHiddenKeys = useMemo(() => debounce(setHiddenKeys, 150), []);

    const notRenderedKeys = new Set(notRenderedKeysArray);
    const distinctKeys = new Set([...notRenderedKeys]);

    for (let i = 0; i < data.length; i++) {
        Object.keys(data[i]).forEach((key) => {
            distinctKeys.add(key);
        });
    }

    notRenderedKeys.forEach((key) => distinctKeys.delete(key));

    const distinctKeysArray = [...distinctKeys];
    const total = getChartDataTotal(data, distinctKeysArray, hiddenKeys);

    const distinctKeysComparable = ['comparableDocCount'];
    const totalComparable = getChartDataTotal(data, distinctKeysComparable, hiddenKeys);

    let trendValue;
    if (totalComparable > 0) {
        trendValue = (total / totalComparable * 100 - 100).toFixed(2);
    } else if (total > 0) {
        trendValue = (100).toFixed(2);
    } else {
        trendValue = (0).toFixed(2);
    }
    const footerAggregationType = getFooterAggregationType(chartQuery);
    const processedData = useMemo(() => {
        let result = [...data];

        if (chartQuery.output && scalableOutputTypes.includes(chartQuery.output)) {
            result = getScaledChartData(result, distinctKeysArray, scalingFactor);
        }

        if (linearRegression) {
            result = addLinearRegressionToData(result, distinctKeysArray, ChartAggregationCalcType.Cumulative, hiddenKeys);
        }

        if (movingAverageDays && movingAverageDays > 0) {
            result = addMovingAverageToData(
                result,
                distinctKeysArray,
                movingAverageDays,
                movingAverageDaysCenter,
                ChartAggregationCalcType.Cumulative,
                hiddenKeys
            );
        }

        return result;
    }, [data, distinctKeys, chartQuery.output, scalingFactor, linearRegression, movingAverageDays, movingAverageDaysCenter, hiddenKeys]);


    if (distinctKeysArray.includes('Other')) {
        if (distinctKeysArray[distinctKeysArray.length - 1] != 'Other') {
            const otherIndex = distinctKeysArray.indexOf('Other');
            distinctKeysArray.splice(otherIndex, 1);
            distinctKeysArray.push('Other');
        }
    }

    return (
        <>
            <AreaCharts
                data={processedData}
                distinctKeysArray={distinctKeysArray}
                chartQueryOutput={chartQuery.output}
                comparablePeriod={comparablePeriod}
                movingAverage={movingAverageDays}
                linearRegression={linearRegression}
                height={height}
                width={width}
                onLegendToggle={debouncedSetHiddenKeys}
                chartSettingsView={chartSettingsView}
                title={title}
                description={description}
                datePeriod={datePeriod}
                dataLabelPosition={dataLabelPosition}
            />

            <ChartIndicators
                total={total}
                totalComparable={totalComparable}
                trendValue={trendValue}
                queryDuration={queryDuration}
                chartQueryOutput={chartQuery.output}
                aggregationType={footerAggregationType}
                chartSettingsView={chartSettingsView}
                onPreviewFooterChange={onPreviewFooterChange}
            />
        </>
    );
});


export default AreaChartWrapper;
